import React from 'react';
import {
  VirtualTableState,
  SortingState,
  FilteringState,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  VirtualTable,
  TableHeaderRow,
  TableColumnResizing,
  TableFilterRow,
} from '@devexpress/dx-react-grid-material-ui';

import AuthService from '../services/auth-service'; 
import LoadingSpinner from '../comp/gen/LoadingSpinner';
import UserAuthRequired from '../comp/gen/UserAuthRequired';
import { useNavigate } from "react-router-dom";

import EcuFwResellerTableProps, { 
  DateTypeProvider, 
  RowNumTypeProvider,
  ResellerTypeProvider,
  CountryTypeProvider,
} from './EcuFwResellerTableProps'

import EcuFwResellerFormDialog from './EcuFwResellerFormDialog'
import EcuFwResellerDeleteDialog from './EcuFwResellerDeleteDialog'

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import { Alert, Button, Paper, Stack } from "@mui/material"
import { requestError } from './ecuFwResellerValidation'
import { DataTypeProvider } from "@devexpress/dx-react-grid"
import notify from 'devextreme/ui/notify';

// ************************************************************

const VIRTUAL_PAGE_SIZE = 50;
const getRowId = row => row.id;

// ************************************************************

export const EcuFwResellerListComponentWrapper = (props) => {
  const navigate = useNavigate()
  return <EcuFwResellerList navigate={navigate} {...props} />
}

class EcuFwResellerList extends React.Component {
  constructor(props) {    
    super(props)    
    this.table_props = new EcuFwResellerTableProps()
    this.state = {
      // dialog
      formDialogOpen: false,
      formDialogSpin: false,
      formDialogEditRow: null,
      deleteDialogOpen: false,
      deleteDialogSpin: false,
      deleteDialogRow: null,
      // resellers
      resellersRows: [],
      // table
      table_total_row_count_ready: false,
      tableRows: [],
      tableSkip: 0,
      requestedTableSkip: 0,
      requestedTableTake: 0,
      tableTotalRowCount: VIRTUAL_PAGE_SIZE * 2,
      tableLoading: false,
      tableError: "",
      tableColumns: [
        ...this.table_props.getTableColumns(),
        { name: 'actions', title: 'Actions', getCellValue: row => row.id },
      ],
      tableColumnExtensions: this.table_props.getTableColumnExtensions(),
      tableSortingStateColumnExtensions: [
        ...this.table_props.getTableSortingStateColumnExtensions(),
        { columnName: 'actions', sortingEnabled: false },
      ],
      tableFilteringStateColumnExtensions: [
        ...this.table_props.getTableFilteringStateColumnExtensions(),
        { columnName: 'actions', filteringEnabled: false },
      ],
      tableColumnWidths: [
        ...this.table_props.getTableColumnWidths(),
        { columnName: 'actions', width: 120 },
      ],
      tableFilters: [],
      tableSorting: [{
        columnName: "ecu_prefix",
        direction: "asc",
      }],    
    }
  }
  
  async getTableRows(requestedTableSkip, take) {
    this.setState({ 
      requestedTableSkip: requestedTableSkip,
      requestedTableTake: take,
      tableLoading: true 
    })    
    try {
      const data = await AuthService.getFwResellerList(requestedTableSkip, take, this.state.tableSorting[0], this.state.tableFilters)
      if (!data.is_valid || !Array.isArray(data.rows)) throw new Error(data.message || "Invalid firmware assignment response")
      var i = requestedTableSkip + 1
      data.rows = data.rows.map(row => {
        row.row_num = i++
        return row
      })
      if (data.is_valid) {
        this.setState({
          tableSkip: data.skip, 
          tableRows: data.rows,
          tableLoading: false,
          tableError: "",
        })
      } else {
        this.setState({ tableLoading: false })
      }
    } catch (e) {
      const message = requestError(e, "Cannot load firmware assignments")
      this.setState({ tableLoading: false, tableError: message })
      notify({ message, position: "top center" }, "error", 3000)
    }
  }

  setTableSorting(value) {
    this.setState({ tableSorting: value })
  }

  async setTableFilters(value) {
    this.setState({ tableFilters: value })    
    try {
      var data = await AuthService.getFwResellerCount(value)
      if (!data.is_valid || !Number.isInteger(data.count)) throw new Error(data.message || "Invalid firmware assignment count")
      this.setState({
        tableTotalRowCount: data.count,
        table_total_row_count_ready: true,
        tableError: "",
      })
    } catch (error) {
      const message = requestError(error, "Cannot count firmware assignments")
      this.setState({ tableError: message })
      notify({ message, position: "top center" }, "error", 3000)
    }
  }

  setTableColumnWidths(value) {
    this.setState({ tableColumnWidths: value })
  }

  async componentDidMount() {
    await this.setTableFilters([])
    // load resellers for dropdown
    AuthService.getResellerList().then(data => {
      if (data.is_valid) {
        var rows = (data.rows || []).map(row => {
          row.view_text = `${row.company_name} (${row.company_country})`
          return row
        })
        this.setState({ resellersRows: rows })
      } else {
        throw new Error(data.message || "Cannot load resellers")
      }
    }).catch(error => {
      notify({ message: requestError(error, "Cannot load resellers"), position: "top center" }, "error", 3000)
    })
  }

  // CRUD handlers
  handleAddClick() {
    this.setState({ formDialogOpen: true, formDialogEditRow: null })
  }

  handleEditClick(row) {
    this.setState({ formDialogOpen: true, formDialogEditRow: row })
  }

  handleDeleteClick(row) {
    this.setState({ deleteDialogOpen: true, deleteDialogRow: row })
  }

  handleFormClose() {
    this.setState({ formDialogOpen: false, formDialogEditRow: null, formDialogSpin: false })
  }

  async handleFormSubmit(data) {
    this.setState({ formDialogSpin: true })
    try {
      var result
      if (data.id) {
        result = await AuthService.updateFwReseller(data)
      } else {
        result = await AuthService.createFwReseller(data)
      }
      if (result.is_valid) {
        notify({ message: "OK", position: "top center" }, "success", 2000)
        this.setState({ formDialogOpen: false, formDialogEditRow: null, formDialogSpin: false })
        // refresh
        await this.setTableFilters(this.state.tableFilters)
        await this.getTableRows(this.state.requestedTableSkip, this.state.requestedTableTake)
      } else {
        notify({ message: result.message || "Error", position: "top center" }, "error", 3000)
        this.setState({ formDialogSpin: false })
      }
    } catch (e) {
      notify({ message: requestError(e, "Request failed"), position: "top center" }, "error", 3000)
      this.setState({ formDialogSpin: false })
    }
  }

  handleDeleteClose() {
    this.setState({ deleteDialogOpen: false, deleteDialogRow: null, deleteDialogSpin: false })
  }

  async handleDeleteSubmit(row) {
    this.setState({ deleteDialogSpin: true })
    try {
      var result = await AuthService.deleteFwReseller(row.id)
      if (result.is_valid) {
        notify({ message: "Deleted", position: "top center" }, "success", 2000)
        this.setState({ deleteDialogOpen: false, deleteDialogRow: null, deleteDialogSpin: false })
        // refresh
        await this.setTableFilters(this.state.tableFilters)
        await this.getTableRows(this.state.requestedTableSkip, this.state.requestedTableTake)
      } else {
        notify({ message: result.message || "Error", position: "top center" }, "error", 3000)
        this.setState({ deleteDialogSpin: false })
      }
    } catch (e) {
      notify({ message: requestError(e, "Request failed"), position: "top center" }, "error", 3000)
      this.setState({ deleteDialogSpin: false })
    }
  }

  render() {     
    if (!this.props.authStatus.completed) return <LoadingSpinner />
    if (this.props.authStatus.user === null) return <UserAuthRequired notlogged />        
    if (!this.props.authStatus.user.is_admin) return <UserAuthRequired notallowed />

    if (!this.state.table_total_row_count_ready) {
      if (this.state.tableError) return <Alert severity="error" action={<Button onClick={() => this.setTableFilters(this.state.tableFilters)}>Retry</Button>}>{this.state.tableError}</Alert>
      return <LoadingSpinner />
    }

    var height = "calc(100vh - " + this.props.navbarHeight + ")"

    const ActionsFormatter = ({ row }) => {
      return (
        <Stack direction="row" spacing={0}>
          <IconButton size="small" onClick={() => this.handleEditClick(row)}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" onClick={() => this.handleDeleteClick(row)} color="error"><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      )
    }
    const ActionsTypeProvider = props => <DataTypeProvider formatterComponent={ActionsFormatter} {...props} />

    return (
      <>
        {this.state.tableError && <Alert severity="error">{this.state.tableError}</Alert>}
        <Paper>
          <Grid 
            rows={this.state.tableRows}
            columns={this.state.tableColumns}
            getRowId={getRowId}
          >
            <RowNumTypeProvider 
              for={[ "row_num" ]} 
            />
            <DateTypeProvider for={[
              "created_at", "updated_at",
            ]} />
            <ResellerTypeProvider for={[ "reseller_id" ]} />
            <CountryTypeProvider for={[ "country_code" ]} />
            <ActionsTypeProvider for={[ "actions" ]} />
            <SortingState
              sorting={this.state.tableSorting}
              onSortingChange={this.setTableSorting.bind(this)}
              columnExtensions={this.state.tableSortingStateColumnExtensions}
            />
            <VirtualTableState 
              loading={this.state.tableLoading}
              totalRowCount={this.state.tableTotalRowCount} 
              pageSize={VIRTUAL_PAGE_SIZE}
              skip={this.state.tableSkip}
              getRows={this.getTableRows.bind(this)}
            />
            <FilteringState
              filters={this.state.tableFilters}
              onFiltersChange={this.setTableFilters.bind(this)}
              columnExtensions={this.state.tableFilteringStateColumnExtensions}
            />
            <VirtualTable 
              height={height}
              columnExtensions={this.state.tableColumnExtensions} 
            />
            <TableColumnResizing
              columnWidths={this.state.tableColumnWidths}
              onColumnWidthsChange={this.setTableColumnWidths.bind(this)}
            />
            <TableHeaderRow 
              showSortingControls
            />
            <TableFilterRow 
              showFilterSelector
            />
          </Grid>
        </Paper>

        <Fab 
          color="primary" 
          aria-label="add" 
          sx={{ position: 'fixed', bottom: 24, right: 24 }}
          onClick={this.handleAddClick.bind(this)}
        >
          <AddIcon />
        </Fab>

        <EcuFwResellerFormDialog
          open={this.state.formDialogOpen}
          spin={this.state.formDialogSpin}
          editRow={this.state.formDialogEditRow}
          resellersRows={this.state.resellersRows}
          onClickClose={this.handleFormClose.bind(this)}
          onClickSubmit={this.handleFormSubmit.bind(this)}
        />

        <EcuFwResellerDeleteDialog
          open={this.state.deleteDialogOpen}
          spin={this.state.deleteDialogSpin}
          deleteRow={this.state.deleteDialogRow}
          onClickClose={this.handleDeleteClose.bind(this)}
          onClickSubmit={this.handleDeleteSubmit.bind(this)}
        />
      </>
    )
  }
}
